import { useState, useEffect } from 'react';
import LoginScreen from './components/LoginScreen';
import ChatInterface from './components/ChatInterface';
import { useWebSocket } from './hooks/useWebSocket';
import { useTheme } from './hooks/useTheme';
import { Message, User } from './types';

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { isDarkMode, toggleTheme } = useTheme();
  
  const { 
    connect, 
    disconnect, 
    sendMessage: wsSendMessage, 
    isConnecting 
  } = useWebSocket({
    onMessage: (message: any) => {
      console.log('Received WebSocket message:', message.type, message);
      switch (message.type) {
        case 'join_success':
        case 'admin_authenticated':
          setIsConnected(true);
          setError(null);
          console.log('Successfully connected to chat');
          break;
          
        case 'error':
          console.error('WebSocket error:', message.message);
          setError(message.message);
          setIsConnected(false);
          break;
          
        case 'history':
          if (message.messages && Array.isArray(message.messages)) {
            console.log('Received message history:', message.messages.length, 'messages');
            setMessages(message.messages);
          } else {
            console.log('No message history received');
            setMessages([]);
          }
          break;
          
        case 'message':
        case 'file':
          console.log('Received new message:', message);
          setMessages(prev => [...prev, message as Message]);
          break;
          
        case 'messages_cleared':
          console.log('All messages cleared');
          setMessages([]);
          break;
          
        case 'message_deleted':
          console.log('Message deleted:', message.messageId);
          setMessages(prev => prev.filter(msg => msg.id !== message.messageId));
          break;
          
        case 'user_joined':
        case 'user_left':
          console.log('User activity:', message.type, message.username);
          break;
          
        default:
          console.log('Unknown message type:', message.type);
      }
    },
    onClose: () => {
      console.log('WebSocket connection closed');
      setIsConnected(false);
      if (currentUser) {
        setError('Connection lost. Please refresh the page.');
      }
    },
    onError: () => {
      console.error('WebSocket connection error');
      setError('Connection error. Please try again.');
    }
  });

  const handleJoin = async (username: string, password?: string, profilePicture?: string) => {
    console.log('Attempting to join chat as:', username);
    const user: User = { username, profilePicture };
    setCurrentUser(user);
    setError(null);
    
    try {
      await connect();
      
      // Send join message after connection is established
      setTimeout(() => {
        console.log('Sending join message for:', username);
        wsSendMessage({
          type: 'join',
          username,
          password,
          profilePicture
        });
      }, 200);
    } catch (err) {
      console.error('Connection error:', err);
      setError('Failed to connect to chat');
      setCurrentUser(null);
    }
  };

  const handleLogout = () => {
    console.log('Logging out user');
    disconnect();
    setCurrentUser(null);
    setMessages([]);
    setIsConnected(false);
    setError(null);
  };

  const sendMessage = (message: string, replyTo?: any) => {
    if (!isConnected) {
      console.warn('Cannot send message: not connected');
      return;
    }
    
    const messageData: any = {
      type: 'message',
      message
    };

    if (replyTo) {
      messageData.replyTo = replyTo.id;
      messageData.replyToMessage = replyTo.message;
      messageData.replyToUsername = replyTo.username;
    }

    console.log('Sending message:', messageData);
    wsSendMessage(messageData);
  };

  const sendFile = (fileName: string, fileUrl: string, fileSize: number, fileType: string, replyTo?: any) => {
    if (!isConnected) {
      console.warn('Cannot send file: not connected');
      return;
    }
    
    const fileData: any = {
      type: 'file',
      fileName,
      fileUrl,
      fileSize,
      fileType
    };

    if (replyTo) {
      fileData.replyTo = replyTo.id;
      fileData.replyToMessage = replyTo.message;
      fileData.replyToUsername = replyTo.username;
    }

    console.log('Sending file:', fileData);
    wsSendMessage(fileData);
  };

  const deleteMessage = (messageId: string) => {
    if (!isConnected) {
      console.warn('Cannot delete message: not connected');
      return;
    }
    
    console.log('Deleting message:', messageId);
    wsSendMessage({
      type: 'delete_message',
      messageId
    });
  };

  const clearAllMessages = () => {
    if (!isConnected) {
      console.warn('Cannot clear messages: not connected');
      return;
    }
    
    console.log('Clearing all messages');
    if (currentUser?.username === 'nepcoder') {
      wsSendMessage({ type: 'clear_all' });
    } else {
      // For non-admin users, use API endpoint
      fetch('/api/delete-all', {
        method: 'POST'
      }).catch(console.error);
    }
  };

  // Apply theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  if (!currentUser || !isConnected) {
    return (
      <LoginScreen 
        onJoin={handleJoin}
        error={error}
        isConnecting={isConnecting}
      />
    );
  }

  return (
    <ChatInterface
      currentUser={currentUser}
      messages={messages}
      onSendMessage={sendMessage}
      onSendFile={sendFile}
      onDeleteMessage={deleteMessage}
      onClearAll={clearAllMessages}
      onLogout={handleLogout}
      isDarkMode={isDarkMode}
      onToggleTheme={toggleTheme}
    />
  );
}

export default App;